import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC, useEffect, useRef, useState } from "react";

import type { Task } from "@recobra/schemas/task";

import { caseSummaryQueryOptions, taskCaseSummaryOf } from "../../cases";
import { TaskCaseSummary, TaskCaseSummarySkeleton } from "./TaskCaseSummary";

export const TaskContextCaseSummary: FC<{
  task: Task;
  labelColumn?: "auto" | "fixed";
}> = ({ task, labelColumn }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(() => typeof IntersectionObserver === "undefined");
  const queryClient = useQueryClient();
  const { data: kase, isPending } = useQuery({
    ...caseSummaryQueryOptions(queryClient, task.contextId),
    enabled: task.contextType === "case" && isVisible,
  });

  useEffect(() => {
    if (task.contextType !== "case") return;

    const container = containerRef.current;
    if (!container || isVisible) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      setIsVisible(true);
      observer.disconnect();
    });
    observer.observe(container);

    return () => observer.disconnect();
  }, [isVisible, task.contextType]);

  if (task.contextType !== "case") return null;

  const caseSummary = kase ? taskCaseSummaryOf(kase) : undefined;
  const isAwaitingCase = !caseSummary && (!isVisible || isPending);

  return (
    <div ref={containerRef}>
      {caseSummary ? <TaskCaseSummary kase={caseSummary} labelColumn={labelColumn} /> : null}
      {isAwaitingCase ? <TaskCaseSummarySkeleton labelColumn={labelColumn} /> : null}
    </div>
  );
};

export const TaskContextSummary: FC<{
  isOpen: boolean;
  contextType: "case" | "client";
  contextId: string;
}> = ({ isOpen, contextType, contextId }) =>
  contextType === "case" ? <CaseContextSummary isOpen={isOpen} contextId={contextId} /> : null;

const CaseContextSummary: FC<{ isOpen: boolean; contextId: string }> = ({ isOpen, contextId }) => {
  const queryClient = useQueryClient();
  const { data: kase, isLoading } = useQuery({
    ...caseSummaryQueryOptions(queryClient, contextId),
    enabled: isOpen && !!contextId,
  });

  return isLoading || !kase ? (
    <TaskCaseSummarySkeleton caseType={kase?.caseType} />
  ) : (
    <TaskCaseSummary kase={taskCaseSummaryOf(kase)} />
  );
};
